import CircularProgress from '@mui/material/CircularProgress'
import Icon from '@mui/material/Icon'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import React from 'react'
import Dropzone, { Accept, DropzoneRef } from 'react-dropzone'

import { acceptList, extensionList } from '@/app/component/atom/audio/uploader'
import { voiceFilterMap } from '@/app/module/campaigns/definitions'
import { useUploadCampaignFiles } from '@/app/module/campaigns/hooks/use-upload-campaign-files'
import { type UploadFilesPayload } from '@/app/module/files/hooks/use-upload-files'

type Props = {
  uploadRef?: React.RefObject<DropzoneRef>
}

const AudioBulkUpload: React.FC<Props> = ({ uploadRef }) => {
  const [upload, { isLoading }] = useUploadCampaignFiles()

  const handleDrop = React.useCallback(
    async (accepted: File[]) => {
      if (!accepted.length) {
        return
      }
      await upload({
        files: accepted,
        filter: AUDIO_BULK_UPLOAD_FILTER,
      })
    },
    [upload],
  )

  return (
    <Root id={NAMESPACE}>
      <Dropzone accept={ACCEPT} maxSize={MAX_FILE_SIZE} multiple ref={uploadRef as any} onDrop={handleDrop}>
        {({ getRootProps, getInputProps }) => (
          <DropArea {...getRootProps()} data-testid="audio-bulk-upload-dropzone">
            <input {...getInputProps()} />
            {isLoading ? (
              <>
                <CircularProgress size={32} thickness={4} />
                <Typography sx={{ mt: 1 }} variant="body2" color="textSecondary">
                  Uploading files, please wait...
                </Typography>
              </>
            ) : (
              <>
                <UploadIcon>cloud_upload</UploadIcon>
                <Typography variant="body1">Drop audio files here or click to select</Typography>
                <Typography color="textSecondary" variant="body2">
                  Supported types: {extensionList.join(', ')} or .zip
                </Typography>
                <Typography color="textSecondary" variant="body2">
                  Max size: {MAX_FILE_SIZE / (1024 * 1024)} MB
                </Typography>
              </>
            )}
          </DropArea>
        )}
      </Dropzone>
    </Root>
  )
}

const Root = styled('div')({
  width: '100%',
})

const DropArea = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  width: '100%',
  minHeight: 140,
  backgroundColor: theme.palette.grey[100],
  borderRadius: 4,
  border: `2px dashed ${theme.palette.grey[300]}`,
  cursor: 'pointer',
  padding: 16,
  textAlign: 'center',
}))

const UploadIcon = styled(Icon)(({ theme }) => ({
  fontSize: '3.5rem',
  color: theme.palette.grey[400],
  marginBottom: theme.spacing(1),
}))

const NAMESPACE = 'audio-bulk-upload'
const MAX_FILE_SIZE = 200 * 1024 * 1024 // 200 MB
const ACCEPT: Accept = { ...acceptList, zip: ['.zip'] }
const AUDIO_BULK_UPLOAD_FILTER = voiceFilterMap.asIs as UploadFilesPayload['filter']

export default AudioBulkUpload

export type { Props as AudioBulkUploadProps }
